import { VoxelSubRange } from '~/domain/services/imageService/gridGeometry';
import { maybeYield } from './utils';

// ── Clearing the parts a dragged window touches ──────────────────────
//
// The rectangle gesture deletes CURVES, not the contents of a box: a part
// the rectangle touches anywhere goes whole, including the stretch of it
// that lies outside the rectangle. This module answers which voxels that
// is, one slice at a time.
//
// It reaches the touched parts by flooding out from the window rather than
// by labelling the volume ({@link labelConnectedComponents}): a labelling
// pass visits every voxel of every slice and allocates an Int32 label per
// voxel, where the gesture only ever needs the handful of parts the drag
// actually landed on.

/**
 * Value written over a cleared voxel: 0, the "far outside" end of the
 * encoded field — the same value {@link SignedDistanceField.clearSlice}
 * writes when a whole slice goes.
 *
 * NOT {@link REMOVED_VALUE}, the just-below-threshold value the
 * component-removal passes use: that one exists so a surface does not
 * over-shrink when the field is REINITIALIZED afterwards, and this write
 * is deliberately not followed by a reinitialization.
 */
const CLEARED_VALUE = 0;

/** What a pass cleared. */
export interface PartsTouchingWindowResult {
    /**
     * Modified copies of the slices that changed, by slice index. Slices
     * the pass did not touch are absent rather than copied, so a caller
     * assembling a new volume can reuse the source's own arrays for them.
     */
    readonly clearedSlices: ReadonlyMap<number, Uint8Array>;
    /** Foreground voxels cleared, for the volume the panel reports. */
    readonly clearedVoxelCount: number;
    /** Connected parts cleared, counted per slice. */
    readonly clearedPartCount: number;
}

/**
 * Clear every in-plane connected part that has at least one foreground
 * voxel inside `range`, on each slice the range spans.
 *
 * Connectivity is 4-connected and IN-PLANE only, the same rule
 * {@link labelConnectedComponents} applies in its '2d' mode, so "a part"
 * means the same shape here as it does in the Parts tab. Slices are
 * independent: a part is cleared on the slices the window spans and on no
 * others, which is what makes the drag's reach the extruded rectangle the
 * user drew.
 *
 * A part is taken whole. The window only decides WHICH parts are taken —
 * once one is touched, the flood follows it wherever it goes on that
 * slice, far outside the rectangle included.
 *
 * The cleared voxels are written as {@link CLEARED_VALUE} and the field is
 * NOT reinitialized, which leaves the deleted part's sub-threshold falloff
 * band behind. That band carries no contour (it is below the iso
 * threshold, so nothing is extracted from it and nothing is drawn), and
 * every operation that consumes distances rather than the iso surface
 * rebuilds the field from that surface first — {@link
 * SDFOperations.applyMarginBase} reinitializes through
 * {@link SdfGenerator.renderSDFFromTexture} before it shifts anything — so
 * no later operation can grow the deleted part back out of its residue.
 *
 * @param pixels one Uint8Array per slice, `nx * ny` values each; not
 * modified.
 * @param threshold the iso value at or above which a voxel is foreground.
 */
export async function clearPartsTouchingWindow(
    pixels: readonly Uint8Array[],
    nx: number,
    ny: number,
    threshold: number,
    range: VoxelSubRange,
    signal?: AbortSignal
): Promise<PartsTouchingWindowResult> {
    const clearedSlices = new Map<number, Uint8Array>();
    let clearedVoxelCount = 0;
    let clearedPartCount = 0;

    // Allocated once for the whole pass and reset per slice: on a large
    // volume this is the difference between two allocations and two per
    // slice, and the flood only ever reads entries it has written this
    // slice.
    const visited = new Uint8Array(nx * ny);
    // Explicit stack rather than recursion: a part can span a whole slice,
    // which is hundreds of thousands of voxels deep in the worst case.
    const stack = new Int32Array(nx * ny);

    let lastYieldTick = performance.now();
    // The window arrives clamped to the grid by {@link gridSubRange}, so
    // it is iterated as given.
    for (let z = range.minZ; z < range.maxZ; z++) {
        const slice = pixels[z];
        let out: Uint8Array | undefined;
        visited.fill(0);

        for (let y = range.minY; y < range.maxY; y++) {
            const rowStart = y * nx;
            for (let x = range.minX; x < range.maxX; x++) {
                const seed = rowStart + x;
                if (visited[seed] === 1 || slice[seed] < threshold) {
                    continue;
                }
                // First voxel of a part this window touches: the copy is
                // made here, on the first actual clear, so a drag over
                // empty space allocates nothing.
                if (!out) {
                    out = new Uint8Array(slice);
                }
                clearedPartCount++;

                let top = 0;
                stack[top++] = seed;
                visited[seed] = 1;
                while (top > 0) {
                    const i = stack[--top];
                    out[i] = CLEARED_VALUE;
                    clearedVoxelCount++;

                    const ix = i % nx;
                    if (
                        ix > 0 &&
                        visited[i - 1] === 0 &&
                        slice[i - 1] >= threshold
                    ) {
                        visited[i - 1] = 1;
                        stack[top++] = i - 1;
                    }
                    if (
                        ix < nx - 1 &&
                        visited[i + 1] === 0 &&
                        slice[i + 1] >= threshold
                    ) {
                        visited[i + 1] = 1;
                        stack[top++] = i + 1;
                    }
                    if (
                        i >= nx &&
                        visited[i - nx] === 0 &&
                        slice[i - nx] >= threshold
                    ) {
                        visited[i - nx] = 1;
                        stack[top++] = i - nx;
                    }
                    if (
                        i + nx < slice.length &&
                        visited[i + nx] === 0 &&
                        slice[i + nx] >= threshold
                    ) {
                        visited[i + nx] = 1;
                        stack[top++] = i + nx;
                    }
                }
            }
        }

        if (out) {
            clearedSlices.set(z, out);
        }
        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }

    return { clearedSlices, clearedVoxelCount, clearedPartCount };
}
